import { usePermissions } from '../contexts/PermissionsContext';

/**
 * Hook to easily check permissions for a specific screen
 * 
 * @param screenCode - The screen code to check permissions for
 * @returns Object with permission check functions
 * 
 * @example
 * const { canView, canCreate, canEdit, canDelete, canExport } = usePermission('CAMERAS');
 * 
 * return (
 *   <div>
 *     {canView && <CameraList />}
 *     {canCreate && <button>Add Camera</button>}
 *     {canEdit && <button>Edit</button>}
 *     {canDelete && <button>Delete</button>}
 *   </div>
 * );
 */
export const usePermission = (screenCode: string) => {
  const { hasPermission } = usePermissions();

  return {
    canView: hasPermission(screenCode, 'view'),
    canCreate: hasPermission(screenCode, 'create'),
    canEdit: hasPermission(screenCode, 'edit'),
    canDelete: hasPermission(screenCode, 'delete'),
    canExport: hasPermission(screenCode, 'export'),
    hasAnyPermission: (actions: Array<'view' | 'create' | 'edit' | 'delete' | 'export'>) => 
      actions.some(action => hasPermission(screenCode, action)),
  };
};
